/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, Input, Output, EventEmitter, OnInit, APP_INITIALIZER, ViewChild } from "@angular/core";
import { ParameterValue } from "../simple-function-action-editor.component";
import { ObjectInstance } from "app/models/object-instance";
import { ObjectService } from "app/services/object.service";
import { Event } from "app/models/event";
import { Property } from "app/models/property";
import { PropertyType } from "app/models/property-type";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";

@Component({
  selector: 'app-function-parameter-editor-object',
  templateUrl: './object-function-parameter-editor.component.html'
})
export class ObjectFunctionParameterEditorComponent implements OnInit {

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  @Input()
  set event(event: Event) {
    this.mEvent = event;
    this.refreshSelectOptions(false, true);
  }

  //Event Trigger for the Rule / Reaction
  mEvent: Event;

  @Input()
  parameterValue: ParameterValue;


  @Output()
  valueChange = new EventEmitter<void>();


  //Boolean used to indicate if the Select Options are ready to be displayed or not
  mRefreshingSelectOptions = true;

  //Available Values for the Parameter Value
  mObjectOptions: SelectOption[] = [];
  mEventOptions: SelectOption[] = [];
  mSelectOptions: SelectOption[] = [];


  get selectedOption(): SelectOption {
    if (this.parameterValue != null && this.parameterValue.mValue != null && this.parameterValue.mValue.length > 0) {
      return this.mSelectOptions.find(option => option.mValue == this.parameterValue.mValue);
    }
    return null;
  }
  set selectedOption(option: SelectOption) {
    if (this.parameterValue != null) {
      this.parameterValue.mValue = option == null? null : option.mValue;
      this.valueChange.emit();
    }
  }



  constructor(
    private mObjectService: ObjectService
  ) { }

  ngOnInit(): void {
    this.refreshSelectOptions(true, true);
  }



  async refreshSelectOptions(refreshObjectOptions: boolean, refreshEventOptions: boolean): Promise<void> {
    this.mRefreshingSelectOptions = true;

    //If needs be, refresh the Object Options
    if (refreshObjectOptions) {
      await this.refreshObjectOptions();
    }
    if (refreshEventOptions) {
      await this.refreshEventOptions();
    }

    //Refresh the Select Options
    const options: SelectOption[] = [];
    options.push(...this.mEventOptions);
    options.push(...this.mObjectOptions);

    if (this.parameterValue != null && this.parameterValue.mValue != null && this.parameterValue.mValue.length > 0) {
      if (options.find(option => option.mValue == this.parameterValue.mValue) == null) {
        const unknownValueOption = new SelectOption();
        unknownValueOption.mValue = this.parameterValue.mValue;
        unknownValueOption.mDisplay = "UNKNOWN VALUE => " + this.parameterValue.mValue;
        options.push(unknownValueOption);
      }
    }
    
    this.mSelectOptions = options;

    this.mRefreshingSelectOptions = false;
  }

  private async refreshObjectOptions(): Promise<void> {
    let objects: ObjectInstance[];
    try {
      objects = await this.mObjectService.getAllObjectInstances();
    } catch (error) {
      this.handleError(error);
    }

    if (objects == null) {
      this.mObjectOptions = [];
    } else {
      const options: SelectOption[] = [];
      for (const object of objects) {
        const option = new SelectOption();
        option.mValue = object.id.toString();
        option.mDisplay = object.object.category + ": " + object.object.name + " - " + object.name;
        options.push(option);
      }
      this.mObjectOptions = options;
    }
  }

  private async refreshEventOptions(): Promise<void> {
    if (this.mEvent == null || this.mEvent.properties == null) {
      this.mEventOptions = [];
    } else {
      const options: SelectOption[] = [];
      for (const eventProperty of this.mEvent.properties) {
        if (eventProperty.type == PropertyType.OBJECT_REFERENCE) {
          const option = new SelectOption();
          option.mValue = "${event." + eventProperty.property + "}";
          option.mDisplay = "FROM EVENT => " + eventProperty.name;
          options.push(option);
        }
      }
      this.mEventOptions = options;
    }
  }

  handleError(error: any): void {
    console.error("handleError() - Error: " + error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString())
    }
  }

}

class SelectOption {
  mValue: string;
  mDisplay: string;
}
